import { useEffect, useState, useMemo } from 'react'
import { EPaymentPlan } from '@if/core/types'
import { MenuItem } from '@mui/material'
import { UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ComputeFundingDto } from '@src/types/ComputePersonalLoanFundingDto'
import { PersonalLoanCalculatorSchemaType } from '@src/types/PersonalLoanCalculatorSchema'
import { useGetPersonalFunding } from '@src/data/api/worksheet-api/personal-loan-worksheet-api'
import { useGetPossibleTerms } from '@src/data/api/worksheet-api/worksheet-api'
import { usePaymentPlanList } from '@src/containers/ViewCreditApplicationPage/worksheet-hooks'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { MerchantPaymentPlan } from '@src/types/MerchantPaymentPlan'
import {
  createCalculationData,
  createComputeMonthTermFrequency,
  shouldAutoSelectTerm,
} from './personalLoanCalculator-utils'

interface TermOption {
  label: string
  value: number
}

interface PaymentOption {
  label: string
  value: EPaymentPlan | string
  id: string
}

interface UseAutoSelectionProps {
  isDataLoading: boolean
  merchantPayments?: PaymentOption[]
  loanTermChoicesList: TermOption[]
  paymentPlanId?: string
  term?: number
  setValue: UseFormSetValue<PersonalLoanCalculatorSchemaType>
}

export const useAutoSelection = ({
  isDataLoading,
  merchantPayments,
  loanTermChoicesList,
  paymentPlanId,
  term,
  setValue,
}: UseAutoSelectionProps) => {
  const [previousPaymentPlanId, setPreviousPaymentPlanId] = useState<string | null>(null)

  useEffect(() => {
    if (!isDataLoading && merchantPayments) {
      if (!paymentPlanId && merchantPayments.length > 0) {
        const firstPaymentPlan = merchantPayments[0].value as EPaymentPlan
        setValue('paymentPlanId', firstPaymentPlan)
        setPreviousPaymentPlanId(firstPaymentPlan)
      }

      if (loanTermChoicesList.length > 0 && shouldAutoSelectTerm(term, paymentPlanId, previousPaymentPlanId)) {
        const firstTerm = loanTermChoicesList[0].value
        setValue('term', firstTerm)
      }

      if (paymentPlanId && paymentPlanId !== previousPaymentPlanId) {
        setPreviousPaymentPlanId(paymentPlanId)
      }
    }
  }, [isDataLoading, merchantPayments, loanTermChoicesList, paymentPlanId, term, setValue, previousPaymentPlanId])

  return {
    previousPaymentPlanId,
  }
}

interface UseCalculatorDataProps {
  amountRequested?: number
  paymentPlanId?: string
  term?: number
  creditRatingInterestRate: number
  merchantId: string
}

export const useCalculatorData = ({
  amountRequested,
  paymentPlanId,
  term,
  creditRatingInterestRate,
  merchantId,
}: UseCalculatorDataProps) => {
  const [calculatorData, setCalculatorData] = useState<ComputeFundingDto | null>(null)

  useEffect(() => {
    if (amountRequested && paymentPlanId && term && creditRatingInterestRate && merchantId) {
      const calculationData = createCalculationData(
        merchantId,
        amountRequested,
        paymentPlanId,
        term,
        creditRatingInterestRate,
      )
      setCalculatorData(calculationData)
    }
  }, [amountRequested, paymentPlanId, term, creditRatingInterestRate, merchantId])

  const [calculatedRate, isFetching] = useGetPersonalFunding(
    'calculator',
    calculatorData,
    calculatorData?.amountRequested || 0,
  )

  const computeMonthTermFrequency = useMemo(() => {
    if (amountRequested && merchantId && paymentPlanId && creditRatingInterestRate) {
      return createComputeMonthTermFrequency(merchantId, paymentPlanId, creditRatingInterestRate, amountRequested)
    }
    return null
  }, [amountRequested, paymentPlanId, creditRatingInterestRate, merchantId])

  const [possibleTerms] = useGetPossibleTerms(computeMonthTermFrequency, amountRequested!)

  return {
    calculatorData,
    calculatedRate,
    isFetching,
    possibleTerms,
  }
}

interface UsePaymentOptionsProps {
  merchant: Merchant | null
  calculatorCreditApp: Partial<CreditApplication>
}

export const usePaymentOptions = ({ merchant, calculatorCreditApp }: UsePaymentOptionsProps) => {
  const { t } = useTranslation()

  const merchantPaymentPlans = useMemo(() => {
    return merchant?.paymentPlans ?? []
  }, [merchant?.paymentPlans])

  const merchantPayments = usePaymentPlanList(
    calculatorCreditApp as CreditApplication,
    merchant || ({} as Merchant),
    merchantPaymentPlans,
  )

  const merchantPaymentOptions = useMemo(
    () =>
      merchantPayments?.map((item) => (
        <MenuItem key={item.value} value={item.value}>
          {item.label.includes(EPaymentPlan.RegularDailyInterests)
            ? t(`EPaymentPlan.${EPaymentPlan.RegularDailyInterests}`, { ns: 'enum' })
            : item.label}
        </MenuItem>
      )),
    [merchantPayments, t],
  )

  return {
    merchantPaymentPlans,
    merchantPayments,
    merchantPaymentOptions,
  } as const
}

interface UseTermOptionsProps {
  paymentPlanId?: EPaymentPlan
  merchantPaymentPlans: MerchantPaymentPlan[]
  possibleTerms?: { monthlyTerms?: { term: number }[] }
}

export const useTermOptions = ({ paymentPlanId, merchantPaymentPlans, possibleTerms }: UseTermOptionsProps) => {
  const { t } = useTranslation()
  const [stableTermOptions, setStableTermOptions] = useState<React.ReactElement[]>([])

  const loanTermChoicesList = useMemo(() => {
    if (paymentPlanId && !paymentPlanId.includes('RegularInterest')) {
      const matchingPaymentPlan = merchantPaymentPlans.find((plan) => plan.id === paymentPlanId)
      if (matchingPaymentPlan?.loanTerm) {
        return [
          {
            label: `${matchingPaymentPlan.loanTerm} ${t('common.months')}`,
            value: matchingPaymentPlan.loanTerm,
          },
        ] as TermOption[]
      }
    }

    return (possibleTerms?.monthlyTerms?.map((item) => ({
      label: `${item.term} ${t('common.months')}`,
      value: item.term,
    })) || []) as TermOption[]
  }, [paymentPlanId, merchantPaymentPlans, possibleTerms, t])

  const termOptions = useMemo(
    () =>
      loanTermChoicesList?.map((loanTerm: TermOption) => (
        <MenuItem key={loanTerm.value} value={loanTerm.value}>
          {loanTerm.label}
        </MenuItem>
      )) || [],
    [loanTermChoicesList],
  )

  useEffect(() => {
    if (termOptions.length > 0) {
      setStableTermOptions(termOptions)
    }
  }, [termOptions])

  return {
    loanTermChoicesList,
    termOptions,
    stableTermOptions,
  } as const
}

export type {
  TermOption,
  PaymentOption,
  UseAutoSelectionProps,
  UseCalculatorDataProps,
  UsePaymentOptionsProps,
  UseTermOptionsProps,
}
